import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import CopyIcon from '@material-ui/icons/FileCopy';
import Editor, { TABLE_CELL_COMPONENT, FORM_FIELD_COMPONENT } from 'tuc-webui-base/components/editors/Editor';
import './ObjectIdEditor.scss';

const MAX_STRING = 10;
const getShortString = (s) => {
  if (!s || !s.length) return '';
  if (s.length <= MAX_STRING) return s;
  const index = s.indexOf('-');
  if ((index >= 4) && (index < MAX_STRING)) {
    return s.substring(0, index);
  }
  return `${s.substring(0, MAX_STRING)}...`;
};

const copyToClipboard = (value) => {
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(value);
  }
};

const ObjectIdTableCell = (props) => {
  const {
    value,
    object,
    getClassName,
    renderIcon,
    icon: Icon,
    getLink,
    getLabel,
    history,
    copyButton,
    getId,
    getTableTooltip,
    getCopyValue,
    getCopyTooltip,
  } = props;
  const id = getId ? getId(object, props) : value;
  const RenderedIcon = renderIcon ? renderIcon(props) : (Icon && <Icon /> || undefined);
  const classname = getClassName && getClassName(id, object) || undefined;
  const link = getLink && getLink(id, object);
  const followLink = useCallback(() => {
    history.push(link);
  }, [link]);
  const label = getLabel ? getLabel(id, object) : getShortString(id);
  const tooltip = getTableTooltip ? getTableTooltip(id, object) : id;

  const Comp = link && id ? (
    tooltip && (
      <Tooltip
        className="grb-objectid-tooltip-ctnr"
        title={id}
        interactive
      >
        <Button
          component={Link}
          color="primary"
          onClick={followLink}
          className={classnames('grb-objectid', 'grb-objectid-link', classname)}
        >
          {RenderedIcon}
          {label}
        </Button>
      </Tooltip>
    ) || (
      <Button
        component={Link}
        color="primary"
        onClick={followLink}
        className={classnames('grb-objectid', 'grb-objectid-link', classname)}
      >
        {RenderedIcon}
        {label}
      </Button>
    )
  ) : (
    <div className={classnames('grb-objectid', classname)}>
      {RenderedIcon}
      {tooltip ? (
        <Tooltip
          className="grb-objectid-tooltip-ctnr"
          title={tooltip}
          interactive
        >
          <Typography variant="body2" className="grb-objectid-short" noWrap>
            {label}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" className="grb-objectid-short" noWrap>
          {label}
        </Typography>
      )}
    </div>
  );

  // Preceded with a condition but on a static value => not breaking rules of Hooks
  const copyToClipboardCB = useCallback(() => {
    // API is well supported: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
    copyToClipboard(getCopyValue ? getCopyValue(id, object) : id);
  }, [id, getCopyValue, object]);
  if (!copyButton || !id) {
    return Comp;
  }
  return (
    <div className="grb-objectid-ctnr">
      {Comp}
      <Tooltip title={getCopyTooltip ? getCopyTooltip(id, object) : 'Copy to clipoard'}>
        <IconButton
          aria-label="toggle password visibility"
          onClick={copyToClipboardCB}
          className="grb-objectid-copy-button"
        >
          <CopyIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

ObjectIdTableCell.propTypes = {
  value: PropTypes.string,
  object: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.string,
  ]).isRequired,
  getClassName: PropTypes.func,
  renderIcon: PropTypes.func,
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  getLink: PropTypes.func,
  getLabel: PropTypes.func,
  getId: PropTypes.func,
  getTableTooltip: PropTypes.func,
  getCopyValue: PropTypes.func,
  getCopyTooltip: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  copyButton: PropTypes.bool,
};

ObjectIdTableCell.defaultProps = {
  value: '',
  getCopyValue: undefined,
  getCopyTooltip: undefined,
  getClassName: undefined,
  renderIcon: undefined,
  icon: undefined,
  getLink: undefined,
  getLabel: undefined,
  getId: undefined,
  getTableTooltip: undefined,
  history: undefined,
  copyButton: true,
};

const ObjectIdTextField = ({ value, label, error, property, readOnly, object, getClassName, getId, ...rest }) => {
  const id = getId ? getId(object, rest) : value;
  const copyToClipboardCB = useCallback(() => {
    // API is well supported: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
    copyToClipboard(id);
  }, [id]);
  const InputProps = {
    readOnly: Boolean(readOnly === undefined ? true : readOnly),
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title="Copy to clipoard">
          <IconButton
            aria-label="toggle password visibility"
            onClick={copyToClipboardCB}
          >
            <CopyIcon />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  };
  const margin = property.margin || 'normal';
  const className = getClassName && getClassName(value);
  return (
    <TextField
      error={!!error}
      label={label}
      className={classnames('grb-object-id-textfield', readOnly && 'grb-readonly', className)}
      value={id === undefined ? ' ' : id}
      margin={margin}
      InputProps={InputProps}
      fullWidth
      helperText={error || (property ? property.description : undefined)}
    />
  );
};

ObjectIdTextField.propTypes = {
  value: PropTypes.string,
  object: PropTypes.shape({}),
  label: PropTypes.string,
  error: PropTypes.string,
  readOnly: PropTypes.bool,
  property: PropTypes.shape({
    description: PropTypes.string,
    margin: PropTypes.string,
  }),
  getClassName: PropTypes.func,
  getId: PropTypes.func,
};

ObjectIdTextField.defaultProps = {
  value: '',
  object: undefined,
  label: '',
  error: undefined,
  property: {},
  readOnly: false,
  getClassName: undefined,
  getId: undefined,
};

const makeComponent = (Component, options, form) => {
  if (options) {
    const compField = form ? 'formComponent' : 'tableComponent';
    if (options[compField]) {
      return options[compField];
    }
    return (props) => {
      return <Component {...props} {...options} />;
    };
  }
  return Component;
};

class ObjectIdEditor extends Editor {
  constructor(name, options) {
    super(name || 'containerId', Object.assign({}, {
      [TABLE_CELL_COMPONENT]: [makeComponent(ObjectIdTableCell, options), makeComponent(ObjectIdTableCell, options)],
      [FORM_FIELD_COMPONENT]: [makeComponent(ObjectIdTextField, options, true)],
    }));
  }

  formatValue(value /* , data, propName, props */) {
    return value;
  }

  getFilterValue(value, formattedValue) {
    return formattedValue;
  }

  getSortValue(value /* ,formattedValue, props */) {
    return value;
  }
}

export { ObjectIdEditor as default, ObjectIdTableCell, getShortString, copyToClipboard };
